import React, { Component } from "react";
import { connect } from "react-redux";
import { Field, reduxForm, formValueSelector } from "redux-form";
import { Form, FormGroup, Row, Col } from "react-bootstrap";
import Steps from "../../../../components/common/steps/Steps";
import Input from "../../../../components/common/input/Input";
import Button from "../../../../components/common/button/Button";
import Radio from "../../../../components/common/radio/Radio";
import Select2 from "../../../../components/common/select/Select2";
import Textarea from "../../../../components/common/textarea/Textarea";
import {
  required,
  requiredFirstName,
  requiredLastName,
  alphaSymbolicSpace,
  dropdown,
  maxLength,
  minLength
} from "../../../../utils/forms/validators";

import AddBox from "./AddBox";
import SocialInputs from "./SocialInputs";

import { searchCitiesAutocomplete } from "../../../../actions/cities.actions";
import { getUserData } from "../../../../actions/user.actions";

import AutoCompleteCity from "./AutoCompleteCity";
import styles from "./CompleteProfile.scss";

const alphaSymbolicSpaceValidation = msg => alphaSymbolicSpace(msg);
const dropdownValidation = msg => dropdown(msg);
const maxLengthValidation = (max, msg) => maxLength(max, msg);

const firstName = alphaSymbolicSpaceValidation(
  "Please enter a valid first name."
);
const lastName = alphaSymbolicSpaceValidation(
  "Please enter a valid last name."
);

const minLengthSummary = minLength(
  150,
  "Your professional summary must be 150-1500 characters long."
);
const maxLengthSummary = maxLengthValidation(
  1500,
  "Your professional summary must be 150-1500 characters long."
);

const primaryIndustry = dropdownValidation("Please select an Industry.");
const industry = maxLengthValidation(
  30,
  "Industry must be less than or equal to 30 characters."
);

const steps = [
  {
    caption: "Your Profile",
    url: "complete-profile"
  },
  {
    caption: "Professional Skills",
    url: "skills"
  },
  {
    caption: "Endorsements",
    url: "endorsements"
  }
];

const INDUSTRY_OTHER = 38;

class CompleteProfile extends Component {
  constructor(props) {
    super(props);

    this.state = {
      currentStep: 1,
      showStep: 0,
      industry: {
        primary: null,
        secondary: null
      }
    };

    this.handleSelect = this.handleSelect.bind(this);
  }

  componentWillMount() {
    this.props.getUserData();
  }

  componentWillReceiveProps(nextProps) {
    if (this.props.user !== nextProps.user) {
      const industry = {
        primary: _.get(nextProps, "user.profile.primary_industry_id"),
        secondary: _.get(nextProps, "user.profile.secondary_industry_id")
      };

      this.setState({ industry: industry });
    }
  }

  handleSelect(value, name) {
    if (value !== INDUSTRY_OTHER) {
      this.props.change(`${name}_industry_other`, null);
    }

    const industry = { ...this.state.industry, [name]: value };
    this.setState({ industry: industry });
  }

  render() {
    const { showStep } = this.state;
    const { handleSubmit, submitting, valid } = this.props;

    return (
      <section className={styles.completeProfile}>
        <div className={styles.header}>
          <Steps steps={steps} activeStep={showStep} />
        </div>
        <div className={styles.content}>
          <h2 className={styles.title}>Complete Your Profile</h2>

          <Form onSubmit={handleSubmit}>
            <div className={styles.form}>
              <FormGroup>
                <div className={styles.subheading}>
                  Tell us a bit more about you so we can match you to the
                  projects that best suit your needs and experience.
                </div>
              </FormGroup>

              <div className={styles.dividerDouble} />
              <div className={styles.sectionTitle}>The Basics</div>

              <FormGroup>
                <Row className={styles.item}>
                  <Col md={6} xs={12} className={styles.itemPadding}>
                    <Field
                      name="first_name"
                      type="text"
                      placeholder="First Name"
                      component={Input}
                      validate={[requiredFirstName, firstName]}
                    />
                  </Col>
                </Row>

                <Row className={styles.item}>
                  <Col md={6} xs={12} className={styles.itemPadding}>
                    <Field
                      name="last_name"
                      type="text"
                      placeholder="Last Name"
                      component={Input}
                      validate={[requiredLastName, lastName]}
                    />
                  </Col>
                </Row>

                <AutoCompleteCity
                  focus={this.props.defaultLocation && true}
                  className={styles.noMargin}
                  cities={this.props.cities}
                  isLoading={this.props.isLoading}
                  onSearchCities={this.props.searchCitiesAutocomplete}
                  selectedValue={this.props.defaultLocation}
                  showPasswordIcon={true}
                  icon="map-marker"
                  commuteCities={this.props.commuteCities}
                  focusOut={selected => {
                    if (!selected) {
                      this.props.change("city", null);
                    }
                  }}
                />

                <div className={styles.lightDivider} />

                <div className={styles.sectionSubtitle}>
                  Would you consider a full-time, on-staff job?
                </div>

                <fieldset className={styles.checkboxContainer}>
                  <Row className={styles.item}>
                    <Col md={7} xs={12} className={styles.itemPadding}>
                      <Field
                        name="full_time_job"
                        component={Radio}
                        type="radio"
                        value={true}
                        text="Yes"
                        parse={val => val === "true"}
                        handleChange={e => (e.target.checked = true)}
                        checked={
                          this.props.initialValues &&
                          this.props.initialValues.full_time_job
                        }
                      />
                    </Col>
                  </Row>

                  <Row className={styles.item}>
                    <Col md={7} xs={12} className={styles.itemPadding}>
                      <Field
                        name="full_time_job"
                        component={Radio}
                        type="radio"
                        value={false}
                        text="No"
                        parse={val => val === "true"}
                        handleChange={e => (e.target.checked = true)}
                        checked={
                          this.props.initialValues &&
                          this.props.initialValues.full_time_job
                        }
                      />
                    </Col>
                  </Row>
                </fieldset>

                <div className={styles.divider} />

                <div className={styles.sectionTitle}>Professional Summary</div>

                <div className={styles.sectionDescription}>
                  Your summary will be visible to hiring managers once you
                  express interest in their project. Tell them in a few words
                  about your professional life and career accomplishments.
                </div>

                <Field
                  name="professional_summary"
                  type="textarea"
                  placeholder=""
                  component={Textarea}
                  minLength={150}
                  maxlength={1500}
                  validate={[required, minLengthSummary, maxLengthSummary]}
                />

                <div className={styles.divider} />

                <AddBox
                  title="Education"
                  name="education"
                  optionName="school_id"
                  inputName="degree"
                  placeholder="Degree"
                  placeholderTypeahead="Education"
                  options={this.props.schools}
                  subtitle="Show off your school spirit! Help companies select
                  you for a project by providing information about your education history."
                  text="Display additional schools..."
                  addText="Add another school + degree"
                  typeahead={true}
                  prefix="Add new School: "
                  isLoading={this.props.isLoadingSchools}
                  labelKey="value"
                  valueKey="school_id"
                  selectOpts={this.props.user && this.props.user.education}
                  allowNew={true}
                  focusOut={(query, selected, name) => {
                    if (
                      !selected ||
                      !Array.isArray(selected) ||
                      !selected.length
                    ) {
                      this.props.change(`${name}`, null);

                      if (query) {
                        this.props.change(`${name}[0].school_name`, query);
                      }
                    }
                  }}
                  onSearch={query => {
                    this.props.onSearchSchools(query);
                  }}
                />

                <div className={styles.divider} />

                <AddBox
                  title="Work History"
                  name="work_history"
                  optionName="company_id"
                  inputName="role"
                  placeholder="Role"
                  placeholderTypeahead="Company Name"
                  options={this.props.companies}
                  subtitle="Let companies know where you´ve worked to help them
                  better understand you career, qualifications, and professional interests."
                  addText="Add another company and role"
                  typeahead={true}
                  prefix="Add new Company: "
                  isLoading={this.props.isLoadingCompany}
                  labelKey="name"
                  valueKey="company_id"
                  selectOpts={this.props.user && this.props.user.work_history}
                  allowNew={true}
                  showYearsWorked={true}
                  noPaddingRight={true}
                  inputNameExtra="years"
                  focusOut={(query, selected, name) => {
                    if (
                      !selected ||
                      !Array.isArray(selected) ||
                      !selected.length
                    ) {
                      this.props.change(`${name}`, null);

                      if (query) {
                        this.props.change(`${name}[0].company_name`, query);
                      }
                    }
                  }}
                  onSearch={query => {
                    this.props.onSearchCompanies(query);
                  }}
                />

                <div className={styles.divider} />

                <div className={styles.sectionTitle}>Industry</div>

                <div className={styles.sectionDescription}>
                  Select the industry or industries where you have had the bulk
                  of your work experience.
                </div>

                <FormGroup>
                  <Row className={styles.item}>
                    <Col md={6} xs={12} className={styles.itemPadding}>
                      <Field
                        name="primary_industry_id"
                        type="select"
                        placeholder="Primary Industry"
                        component={Select2}
                        options={this.props.industries}
                        onSelectChange={e => this.handleSelect(e, "primary")}
                        validate={primaryIndustry}
                      />
                    </Col>
                    {this.state.industry.primary === INDUSTRY_OTHER && (
                      <Col md={6} xs={12} className={styles.itemPadding}>
                        <Field
                          name="primary_industry_other"
                          type="text"
                          placeholder="Industry"
                          component={Input}
                          validate={[required, industry]}
                        />
                      </Col>
                    )}
                  </Row>

                  <Row className={styles.item}>
                    <Col md={6} xs={12} className={styles.itemPadding}>
                      <Field
                        name="secondary_industry_id"
                        type="select"
                        placeholder="Secondary Industry (Optional)"
                        component={Select2}
                        options={this.props.industries}
                        onSelectChange={e => this.handleSelect(e, "secondary")}
                      />
                    </Col>
                    {this.state.industry.secondary === INDUSTRY_OTHER && (
                      <Col md={6} xs={12} className={styles.itemPadding}>
                        <Field
                          name="secondary_industry_other"
                          type="text"
                          placeholder="Industry"
                          component={Input}
                          validate={[required, industry]}
                        />
                      </Col>
                    )}
                  </Row>
                </FormGroup>

                <div className={styles.divider} />

                <div className={styles.sectionTitle}>
                  Resumé, LinkedIn, and More
                </div>

                <div className={styles.sectionDescription}>
                  All the news that can´t fit: upload a resume (max 2MB), link
                  out your portfolio or personal website, and share your
                  LinkedIn profile.
                </div>

                <SocialInputs />

                <div className={styles.dividerDouble} />

                <Button
                  isPrimary
                  type="submit"
                  size={"large"}
                  label="SAVE + CONTINUE >"
                  isLoading={this.props.saving}
                  loadingText="Saving..."
                  isDisabled={!valid || submitting || this.props.saving}
                />
              </FormGroup>
            </div>
          </Form>
        </div>
      </section>
    );
  }
}

CompleteProfile = reduxForm({
  form: "completeProfile",
  enableReinitialize: true,
  touchOnBlur: true,
  touchOnChange: true
})(CompleteProfile);

const selector = formValueSelector("completeProfile");
CompleteProfile = connect(
  state => {
    const defaultLocation = selector(state, "city_obj");
    return {
      defaultLocation,
      user: state.user.data,
      initialValues: state.user.data,
      cities: state.cities.cities.predictions,
      isLoading: state.cities.cities.isLoading,
      saving: state.user.loading
    };
  },
  dispatch => {
    return {
      getUserData: () => dispatch(getUserData()),
      searchCitiesAutocomplete: query =>
        dispatch(searchCitiesAutocomplete(query))
    };
  }
)(CompleteProfile);

export default CompleteProfile;



// WEBPACK FOOTER //
// ./src/routes/candidate/completeProfile/components/CompleteProfile.js